export const LISTING_PAYMENT_STATUSES = [
  "created",
  "initiating",
  "pending",
  "succeeded",
  "failed",
  "cancelled",
  "timed_out",
  "verification_required",
] as const;

export type ListingPaymentStatus =
  (typeof LISTING_PAYMENT_STATUSES)[number];

export type InitiateListingPaymentRequest = {
  phoneNumber: string;
  idempotencyKey: string;
  configurationVersion: number;
  configurationFingerprint: string;
};

export type InitiateListingPaymentResponse = {
  paymentId: string;
  status: ListingPaymentStatus;
};

export type GetListingPaymentConfigurationRequest = Record<string, never>;

export type ListingPaymentConfiguration = {
  planId: "basic_business_listing_annual";
  currency: "KES";
  canonicalAmount: 5000;
  effectiveAmount: number;
  environment: "sandbox" | "production";
  isSandboxTestPayment: boolean;
  configurationVersion: number;
  configurationFingerprint: string;
};

export type ListingPaymentConfigurationLoadState =
  | { status: "loading"; configuration: null }
  | { status: "error"; configuration: null }
  | {
      status: "ready";
      configuration: ListingPaymentConfiguration;
    };

export type GetListingPaymentStatusRequest = {
  paymentId: string;
};

export type GetListingPaymentStatusResponse = {
  paymentId: string;
  ownerId: string;
  planId: string;
  planName: string;
  amount: number;
  canonicalPlanAmount?: 5000;
  currency: "KES";
  environment?: "sandbox" | "production";
  isSandboxTestPayment?: boolean;
  method: "mpesa_paybill_stk_push";
  status: ListingPaymentStatus;
  maskedPhone: string;
  mpesaReceiptNumber?: string;
  resultCategory?: string;
  resultMessage?: string;
  entitlementId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  completedAt?: unknown;
};

export type RecoveredListingPayment = {
  paymentId: string;
  planId: "basic_business_listing_annual";
  planName: "Basic Business Listing";
  amount: 1 | 5000;
  canonicalPlanAmount?: 5000;
  currency: "KES";
  environment?: "sandbox" | "production";
  isSandboxTestPayment?: boolean;
  method: "mpesa_paybill_stk_push";
  status: ListingPaymentStatus;
  maskedPhone: string;
  entitlementId?: string;
};

export type RecoverCurrentListingPaymentRequest = Record<string, never>;

export type RecoverCurrentListingPaymentResponse =
  | { status: "none" }
  | {
      status: Exclude<ListingPaymentStatus, "verification_required">;
      payment: RecoveredListingPayment;
    }
  | {
      status: "verification_required";
      payment?: RecoveredListingPayment;
    }
  | {
      status: "consumed";
      payment: RecoveredListingPayment;
      entitlementId: string;
      businessId: string;
    };

export type CreatePaidListingDraftRequest = {
  entitlementId: string;
  ownerName: string;
  businessName: string;
  mobileNumber: string;
};

export type CreatePaidListingDraftResponse = {
  businessId: string;
  status: "consumed";
};

export type ListingPaymentDraftSeed = {
  ownerName: string;
  businessName: string;
  mobileNumber: string;
};

export type PersistedListingPaymentIntent = {
  version: 2;
  ownerScopeHash: string;
  idempotencyKey: string;
  createdAtMs: number;
  expiresAtMs: number;
};

export type ListingPaymentIntentReadStatus =
  | "missing"
  | "valid"
  | "expired"
  | "malformed";

export type ListingPaymentIntentReadResult = {
  status: ListingPaymentIntentReadStatus;
  intent: PersistedListingPaymentIntent | null;
  legacySessionPresent: boolean;
};

/**
 * In-memory state only. This object must never be written to AsyncStorage.
 */
export type ListingPaymentRuntimeSession = {
  uid: string;
  intent?: PersistedListingPaymentIntent;
  phoneNumber?: string;
  paymentId?: string;
  entitlementId?: string;
  businessId?: string;
  maskedPhone?: string;
  lastStatus?: ListingPaymentStatus;
  manualReviewRequired?: boolean;
};
